import { useCallback } from 'react';
import { t, plural } from '@lingui/core/macro';
import type { Bookmark, BookmarkActionViewMode } from '../../../../types/api';
import { useOptionalBookmarkEdit } from '../../../../contexts/BookmarkEditContext';
import { useBookmarkPageContent } from '../../../../contexts/BookmarkPageContext';
import {
  useArchiveBookmark,
  useDeleteBookmark,
  useLikeBookmark,
  useUnarchiveBookmark,
} from '../../../../services/bookmarkActions';
import {
  focusBookmarkRow,
  resetBookmarkListFocus,
  setFocusedBookmarkId,
} from '../../../../services/bookmarkListFocus';
import { useToast } from '../../../../hooks/useToast';
import { SHORTCUT_KEYS } from '../../../KeyboardShortcutsModal/shortcuts';

/** Which view the row's "..." popover opens into. */
export type BookmarkActionsView = 'menu' | 'folders' | 'tags';

interface UseBookmarkRowShortcutsOptions {
  bookmark: Bookmark;
  viewMode: BookmarkActionViewMode;
  /** False in batch mode, on mobile, on profiles, or while the "..." menu is open. */
  enabled: boolean;
  /** Opens the row's actions popover, optionally straight into a sub-view. */
  openActions: (view: BookmarkActionsView) => void;
}

/**
 * Single-key actions for the keyboard-focused row in the 2-column (list-only)
 * article list: the same keys the reader uses, applied to the focused article.
 *
 * Bound to the row rather than the document so it targets whichever row has
 * focus, and so it stays off in 3col/full where useReaderKeyboardShortcuts
 * already owns these keys for the open article.
 *
 * When editing this list, also update the "Article list" section in
 * src/components/KeyboardShortcutsModal/shortcuts.ts.
 */
export function useBookmarkRowShortcuts({
  bookmark,
  viewMode,
  enabled,
  openActions,
}: UseBookmarkRowShortcutsOptions) {
  const bookmarkPage = useBookmarkPageContent();
  const bookmarkEdit = useOptionalBookmarkEdit();
  const { showToast } = useToast();
  const likeMutation = useLikeBookmark();
  const archiveMutation = useArchiveBookmark();
  const unarchiveMutation = useUnarchiveBookmark();
  const deleteMutation = useDeleteBookmark();

  const bookmarks = bookmarkPage?.bookmarks;

  // A shortcut fires with no click to trace it back to, and liking leaves the
  // row looking identical, so confirm what happened. Failures are already
  // surfaced by the mutation hooks' own error toasts.
  const confirmed = useCallback(
    (message: string) => ({ onSuccess: () => showToast(message, 'success') }),
    [showToast],
  );

  // Archiving or deleting removes this row, which would drop DOM focus to the
  // body and strand the keyboard user. Move the cursor to the neighbouring row
  // first — it survives the re-render, so focusing it now is safe.
  const advanceFocusPastRow = useCallback(() => {
    const list = bookmarks ?? [];
    const index = list.findIndex((b) => String(b.id) === String(bookmark.id));
    const neighbor = index === -1 ? undefined : (list[index + 1] ?? list[index - 1]);
    if (!neighbor) {
      resetBookmarkListFocus();
      return;
    }
    setFocusedBookmarkId(neighbor.id);
    focusBookmarkRow(String(neighbor.id), { markHighlight: true });
  }, [bookmarks, bookmark.id]);

  const handleShortcutKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!enabled) return;
      // Enter/Space (row open/select) and the menu's own accelerators both
      // preventDefault before this bubbles up.
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;

      const target = event.target as HTMLElement;
      if (target.matches?.('input, textarea, select, [contenteditable]')) return;

      // Lower-cased so Caps Lock doesn't disable the shortcuts.
      switch (event.key.toLowerCase()) {
        case SHORTCUT_KEYS.like: {
          event.preventDefault();
          const unliking = bookmark.liked;
          likeMutation.mutate(
            { bookmarkId: bookmark.id, action: unliking ? 'unlike' : 'like' },
            unliking
              ? undefined
              : confirmed(
                  plural(1, { one: 'Liked # article', other: 'Liked # articles' }),
                ),
          );
          return;
        }
        case SHORTCUT_KEYS.archive: {
          // The liked view has no archive/unarchive action (cf. the "..." menu).
          if (viewMode === 'liked') return;
          event.preventDefault();
          advanceFocusPastRow();
          if (viewMode === 'archive') {
            unarchiveMutation.mutate(
              { bookmarkId: bookmark.id },
              confirmed(
                plural(1, {
                  one: 'Unarchived # article',
                  other: 'Unarchived # articles',
                }),
              ),
            );
          } else {
            archiveMutation.mutate(
              { bookmarkId: bookmark.id },
              confirmed(
                plural(1, { one: 'Archived # article', other: 'Archived # articles' }),
              ),
            );
          }
          return;
        }
        case SHORTCUT_KEYS.delete: {
          event.preventDefault();
          if (!window.confirm(t`Are you sure you want to delete this article?`)) return;
          advanceFocusPastRow();
          deleteMutation.mutate(
            { bookmarkId: bookmark.id },
            confirmed(
              plural(1, { one: 'Deleted # article', other: 'Deleted # articles' }),
            ),
          );
          return;
        }
        case SHORTCUT_KEYS.edit: {
          if (!bookmarkEdit) return;
          event.preventDefault();
          bookmarkEdit.openEditModal(bookmark);
          return;
        }
        case SHORTCUT_KEYS.move: {
          event.preventDefault();
          openActions('folders');
          return;
        }
        case SHORTCUT_KEYS.tag: {
          event.preventDefault();
          openActions('tags');
          return;
        }
      }
    },
    [
      enabled,
      bookmark,
      viewMode,
      openActions,
      advanceFocusPastRow,
      bookmarkEdit,
      confirmed,
      likeMutation,
      archiveMutation,
      unarchiveMutation,
      deleteMutation,
    ],
  );

  return { handleShortcutKeyDown };
}
